import { useEffect, useState, useCallback } from 'react';
import { useAdminStore, SHOW_ENV_TOGGLE } from '@/lib/stores/adminStore';
import type { HealthEnv, ServiceStatus } from '@/lib/stores/adminStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Activity,
  AlertTriangle,
  Server,
  Shield,
  Users,
  Swords,
  RefreshCw,
  UserCircle2,
  Copy,
} from 'lucide-react';
import type {
  AdminUserInfo,
  AdminParty,
  ErrorEntry,
  AdminGameStatusResponse,
  PlayerCharSummary,
} from '@/lib/api';
import ImageModal from '@/components/common/ImageModal';

function StatusDot({ status }: { status: string }) {
  const color =
    status === 'ok'
      ? 'bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.6)]'
      : status === 'n/a'
        ? 'bg-zinc-500'
        : 'bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.6)]';
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${color}`} />;
}

function ServiceHealthCard({
  services,
  healthEnv,
  onToggleEnv,
}: {
  services: ServiceStatus[];
  healthEnv: HealthEnv;
  onToggleEnv: (env: HealthEnv) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Server className="h-4 w-4 text-muted-foreground" />
          Services
          {SHOW_ENV_TOGGLE ? (
            <div className="ml-auto flex gap-1">
              <Button
                variant={healthEnv === 'local' ? 'default' : 'ghost'}
                size="sm"
                className="h-5 px-2 text-[10px]"
                onClick={() => onToggleEnv('local')}
              >
                Local
              </Button>
              <Button
                variant={healthEnv === 'staging' ? 'default' : 'ghost'}
                size="sm"
                className="h-5 px-2 text-[10px]"
                onClick={() => onToggleEnv('staging')}
              >
                Staging
              </Button>
            </div>
          ) : (
            <Badge variant="secondary" className="ml-auto text-[10px]">
              Staging
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {services.length === 0
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <StatusDot status="unknown" />
                <span className="text-muted-foreground">...</span>
              </div>
            ))
          : services.map((s) => (
              <div key={s.name} className="flex items-center gap-2 text-sm">
                <StatusDot status={s.status} />
                <span className="w-16 shrink-0">{s.name}</span>
                <span className="text-muted-foreground text-[10px] font-mono truncate">
                  {s.url?.replace(/^https?:\/\//, '') ?? ''}
                </span>
              </div>
            ))}
      </CardContent>
    </Card>
  );
}

function StatsCard({
  userCount,
  partyCount,
  sessionCount,
}: {
  userCount: number;
  partyCount: number;
  sessionCount: number;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Activity className="h-4 w-4 text-muted-foreground" />
          Stats
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Users</span>
          <span className="font-mono">{userCount}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Parties</span>
          <span className="font-mono">{partyCount}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Sessions</span>
          <span className="font-mono">{sessionCount}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function ServerInfoCard({ gameStatus }: { gameStatus: AdminGameStatusResponse | null }) {
  if (!gameStatus) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" />
            Game Server
          </CardTitle>
        </CardHeader>
        <CardContent>
          <span className="text-sm text-muted-foreground">Loading...</span>
        </CardContent>
      </Card>
    );
  }

  const { stats, features, version, environment } = gameStatus;
  const capacityPct = Math.round(stats.capacityPercent);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Shield className="h-4 w-4 text-muted-foreground" />
          Game Server
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="text-xs text-muted-foreground font-mono truncate">
          {import.meta.env.VITE_GAME_SERVER_URL || 'http://localhost:3000'}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Version</span>
          <Badge variant="outline" className="ml-auto font-mono text-xs">
            {version}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">Env</span>
          <Badge variant="secondary" className="ml-auto text-xs">
            {environment}
          </Badge>
        </div>
        <div className="text-sm">
          <div className="flex justify-between mb-1">
            <span className="text-muted-foreground">Capacity</span>
            <span className="font-mono text-xs">
              {stats.activeSessions}/{stats.maxSessions} ({capacityPct}%)
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${capacityPct > 80 ? 'bg-red-500' : capacityPct > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(capacityPct, 100)}%` }}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-3 pt-1">
          {[
            { name: 'Gemini', on: features.aiEngines },
            { name: 'Elastic', on: features.search },
            { name: 'WS', on: features.websocket },
            { name: 'FS', on: features.firestore },
          ].map((f) => (
            <div key={f.name} className="flex items-center gap-1 text-xs">
              <StatusDot status={f.on ? 'ok' : 'down'} />
              <span className={f.on ? '' : 'text-muted-foreground'}>{f.name}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function UsersTable({ users }: { users: AdminUserInfo[] }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          Users
          <Badge variant="secondary" className="ml-auto text-xs">
            {users.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-t text-muted-foreground text-xs">
                <th className="text-left font-medium px-4 py-2">Email</th>
                <th className="text-left font-medium px-4 py-2">Name</th>
                <th className="text-right font-medium px-4 py-2">PMT</th>
                <th className="text-center font-medium px-4 py-2">Char</th>
                <th className="text-center font-medium px-4 py-2">Role</th>
                <th className="text-right font-medium px-4 py-2">Last Login</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.uid} className="border-t hover:bg-muted/50 transition-colors">
                  <td className="px-4 py-2 font-mono text-xs truncate max-w-[200px]">{u.email}</td>
                  <td className="px-4 py-2">{u.displayName ?? '—'}</td>
                  <td className="px-4 py-2 text-right font-mono">{u.pmTokenBalance.toFixed(2)}</td>
                  <td className="px-4 py-2 text-center">{u.charId ? '✅' : ''}</td>
                  <td className="px-4 py-2 text-center">{u.isAdmin ? '👑' : ''}</td>
                  <td className="px-4 py-2 text-right text-xs text-muted-foreground whitespace-nowrap">
                    {u.lastLoginAt ? formatRelativeDate(u.lastLoginAt) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function PartiesTable({
  parties,
  onShowImage,
}: {
  parties: AdminParty[];
  onShowImage: (url: string, alt: string) => void;
}) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Swords className="h-4 w-4 text-muted-foreground" />
          Parties
          <Badge variant="secondary" className="ml-auto text-xs">
            {parties.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-t text-muted-foreground text-xs">
                <th className="text-left font-medium px-4 py-2">Name</th>
                <th className="text-left font-medium px-4 py-2">Leader</th>
                <th className="text-center font-medium px-4 py-2">Members</th>
                <th className="text-left font-medium px-4 py-2">Module</th>
                <th className="text-left font-medium px-4 py-2">Status</th>
                <th className="text-right font-medium px-4 py-2">Created</th>
                <th className="text-center font-medium px-4 py-2">Image</th>
              </tr>
            </thead>
            <tbody>
              {parties.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-center text-muted-foreground">
                    No parties
                  </td>
                </tr>
              ) : (
                parties.map((p) => {
                  const leader = p.leaderCharacterId
                    ? p.members.find((m) => m.id === p.leaderCharacterId)
                    : undefined;
                  return (
                    <tr key={p.id} className="border-t hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-2">{p.name}</td>
                      <td className="px-4 py-2 text-muted-foreground">{leader?.name ?? '—'}</td>
                      <td className="px-4 py-2 text-center font-mono">
                        {p.memberCharacterIds.length}
                      </td>
                      <td className="px-4 py-2">
                        {p.selectedModuleName ?? p.selectedModuleId ?? '—'}
                      </td>
                      <td className="px-4 py-2">
                        <Badge
                          variant={p.status === 'in_game' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {p.status ?? 'idle'}
                        </Badge>
                      </td>
                      <td className="px-4 py-2 text-right text-xs text-muted-foreground whitespace-nowrap">
                        {p.createdAt ? formatRelativeDate(p.createdAt) : '—'}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {p.partyImageUrl ? (
                          <button
                            type="button"
                            onClick={() => onShowImage(p.partyImageUrl!, `${p.name} party image`)}
                            className="text-xs underline text-muted-foreground hover:text-foreground"
                          >
                            view
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function PlayerCharsTable({
  chars,
  users,
  onShowImage,
}: {
  chars: PlayerCharSummary[];
  users: AdminUserInfo[];
  onShowImage: (url: string, alt: string) => void;
}) {
  const usersByUid = new Map(users.map((u) => [u.uid, u]));
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const copyId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId((p) => (p === id ? null : p)), 1200);
    } catch {
      /* ignore */
    }
  };

  const summary = (c: PlayerCharSummary) => {
    const parts = [];
    if (c.level !== null) parts.push(`Level ${c.level}`);
    if (c.species) parts.push(c.species);
    if (c.class) parts.push(c.class);
    return parts.join(' ') || '—';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <UserCircle2 className="h-4 w-4 text-muted-foreground" />
          Player Characters
          <Badge variant="secondary" className="ml-auto text-xs">
            {chars.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-t text-muted-foreground text-xs">
                <th className="text-left font-medium px-4 py-2">Avatar</th>
                <th className="text-left font-medium px-4 py-2">Name</th>
                <th className="text-left font-medium px-4 py-2">Summary</th>
                <th className="text-left font-medium px-4 py-2">Player</th>
                <th className="text-right font-medium px-4 py-2">Created</th>
                <th className="text-center font-medium px-4 py-2">Portrait</th>
                <th className="text-right font-medium px-4 py-2">Char ID</th>
              </tr>
            </thead>
            <tbody>
              {chars.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-center text-muted-foreground">
                    No player characters
                  </td>
                </tr>
              ) : (
                chars.map((c) => {
                  const owner = usersByUid.get(c.ownerUserId);
                  const playerLabel =
                    owner?.displayName && owner?.email
                      ? `${owner.displayName} (${owner.email})`
                      : (owner?.email ?? c.ownerUserId);
                  return (
                    <tr key={c.charId} className="border-t hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-2">
                        {c.avatarUrl ? (
                          <button
                            type="button"
                            onClick={() => onShowImage(c.avatarUrl!, `${c.name} avatar`)}
                            className="block h-9 w-9 overflow-hidden rounded-full border border-border hover:ring-2 hover:ring-primary/60"
                            aria-label={`View ${c.name} avatar`}
                          >
                            <img src={c.avatarUrl} alt="" className="h-full w-full object-cover" />
                          </button>
                        ) : (
                          <div className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-muted text-base">
                            {c.avatarEmoji ?? '🧑'}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 font-medium">{c.name}</td>
                      <td className="px-4 py-2 text-muted-foreground">{summary(c)}</td>
                      <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-[280px]">
                        {playerLabel}
                      </td>
                      <td className="px-4 py-2 text-right text-xs text-muted-foreground whitespace-nowrap">
                        {c.createdAt ? formatRelativeDate(c.createdAt) : '—'}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {c.portraitUrl ? (
                          <button
                            type="button"
                            onClick={() => onShowImage(c.portraitUrl!, `${c.name} portrait`)}
                            className="text-xs underline text-muted-foreground hover:text-foreground"
                          >
                            view
                          </button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => copyId(c.charId)}
                          title={c.charId}
                          aria-label="Copy character ID"
                          className="inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground"
                        >
                          <Copy className="h-3 w-3" />
                          {copiedId === c.charId ? 'copied' : 'copy'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function levelColor(level: string): string {
  switch (level.toUpperCase()) {
    case 'ERROR':
      return 'text-red-500';
    case 'WARN':
      return 'text-amber-500';
    default:
      return 'text-muted-foreground';
  }
}

type SortKey = 'timestamp' | 'source' | 'level' | 'target' | 'message';
type SortDir = 'asc' | 'desc';

function SortableHeader({
  label,
  sortKey,
  currentKey,
  dir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const active = sortKey === currentKey;
  return (
    <th
      className="text-left font-medium px-4 py-2 cursor-pointer hover:text-foreground select-none"
      onClick={() => onSort(sortKey)}
    >
      {label} {active ? (dir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );
}

function ErrorsTable({ errors }: { errors: (ErrorEntry & { source: string })[] }) {
  const [sortKey, setSortKey] = useState<SortKey>('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = useCallback(
    (key: SortKey) => {
      if (key === sortKey) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
    },
    [sortKey]
  );

  const sorted = [...errors].sort((a, b) => {
    const va = a[sortKey];
    const vb = b[sortKey];
    const cmp = va.localeCompare(vb);
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          Recent Errors
          <Badge variant="secondary" className="ml-auto text-xs">
            {errors.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card">
              <tr className="border-t text-muted-foreground text-xs">
                <SortableHeader
                  label="Time"
                  sortKey="timestamp"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Source"
                  sortKey="source"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Level"
                  sortKey="level"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Target"
                  sortKey="target"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Message"
                  sortKey="message"
                  currentKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-3 text-center text-muted-foreground">
                    No recent errors
                  </td>
                </tr>
              ) : (
                sorted.map((e, i) => (
                  <tr
                    key={i}
                    className="border-t hover:bg-muted/50 transition-colors cursor-pointer"
                    onClick={() => {
                      const text = `[${e.timestamp}] [${e.source}] ${e.level} ${e.target}: ${e.message}`;
                      navigator.clipboard.writeText(text);
                    }}
                    title="Click to copy"
                  >
                    <td className="px-4 py-2 font-mono text-xs whitespace-nowrap">
                      {formatTime(e.timestamp)}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {e.source}
                      </Badge>
                    </td>
                    <td className={`px-4 py-2 font-mono text-xs font-bold ${levelColor(e.level)}`}>
                      {e.level}
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground truncate max-w-[150px]">
                      {e.target}
                    </td>
                    <td className="px-4 py-2 text-xs truncate max-w-[300px]">{e.message}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function formatRelativeDate(iso: string): string {
  try {
    const d = new Date(iso);
    const now = Date.now();
    const diffMs = now - d.getTime();
    const diffMins = Math.floor(diffMs / 60_000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function formatTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return iso;
  }
}

function RefreshIndicator({
  lastFetchedAt,
  onRefresh,
  loading,
}: {
  lastFetchedAt: number | null;
  onRefresh: () => void;
  loading: boolean;
}) {
  const ago = lastFetchedAt ? Math.round((Date.now() - lastFetchedAt) / 1000) : null;
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {ago !== null && <span>{ago < 5 ? 'just now' : `${ago}s ago`} · 30s</span>}
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={onRefresh}
        aria-label="Refresh dashboard"
        title="Refresh dashboard"
      >
        <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
      </Button>
    </div>
  );
}

export default function AdminPage() {
  const {
    overview,
    gameStatus,
    lobbyErrors,
    services,
    healthEnv,
    setHealthEnv,
    loading,
    error,
    lastFetchedAt,
    startAutoRefresh,
    fetch: refresh,
  } = useAdminStore();

  useEffect(() => {
    const stop = startAutoRefresh();
    return stop;
  }, [startAutoRefresh]);

  const [modal, setModal] = useState<{ url: string; alt: string } | null>(null);
  const showImage = useCallback((url: string, alt: string) => setModal({ url, alt }), []);

  const allErrors = [
    ...(overview?.recentErrors ?? []).map((e) => ({ ...e, source: 'API' as const })),
    ...(gameStatus?.recentErrors ?? []).map((e) => ({ ...e, source: 'Game' as const })),
    ...lobbyErrors.map((e) => ({ ...e, source: 'Lobby' as const })),
  ].sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <span className="text-accent-gold">☥</span> Admin Dashboard
        </h1>
        <RefreshIndicator
          lastFetchedAt={lastFetchedAt}
          onRefresh={() => refresh()}
          loading={loading}
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Top row: Service Health + Stats + Server Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ServiceHealthCard services={services} healthEnv={healthEnv} onToggleEnv={setHealthEnv} />
        <StatsCard
          userCount={overview?.users.length ?? 0}
          partyCount={overview?.parties.length ?? 0}
          sessionCount={gameStatus?.stats.activeSessions ?? 0}
        />
        <ServerInfoCard gameStatus={gameStatus} />
      </div>

      {/* Users table */}
      {loading && !overview ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">Loading...</CardContent>
        </Card>
      ) : overview ? (
        <>
          <UsersTable users={overview.users} />
          <PartiesTable parties={overview.parties} onShowImage={showImage} />
          <PlayerCharsTable
            chars={overview.playerCharacters}
            users={overview.users}
            onShowImage={showImage}
          />
        </>
      ) : null}

      {/* Errors — merged from API + game server */}
      <ErrorsTable errors={allErrors} />

      <ImageModal
        isOpen={modal !== null}
        onClose={() => setModal(null)}
        imageUrl={modal?.url}
        altText={modal?.alt ?? ''}
      />
    </div>
  );
}
